import _ from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';
import { fetchHolterEvents, formatEventStrip, updateEventChangeAndNew } from '@/entities/event';
import { fetchHolterRrHeatMapCount } from '@/entities/beat';
import { KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { toastrError } from '@/shared/lib/toastNotification';
import { logError } from '@/shared/lib/apiHelpers';
import { DATA_HEATMAP_STATUS } from '@/shared/model';
import type { IReportInfo } from '@/shared/model';
import {
  selectActiveTab,
  selectEventChanges,
  selectEventNews,
  useReportStore,
} from '@/entities/report';
import { formatHolterRrHeatMapCount } from './handler';

/**
 * Backs the RR-interval tab shell, supplying report identity, the active beat button and the heatmap data behind it. Loads the RR heatmap counts for the active button on each switch while tracking initial, loading, empty and success states, reports failures through a toast, and exposes a callback that fetches artifact events for a list of days into the combined heatmap map.
 *
 * @returns The tab context: the active button, its heatmap status, study and profile ids, report start and stop, timezone offset, the ECG data map and the day-list fetch callback.
 */
const useRrInterval = () => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setDataHeatmapChart = useReportStore((s) => s.actions.setDataHeatmapChart);
  const dataHeatmapChartStatus = useReportStore((s) => s.dataHeatmapChartStatus);
  const setDataHeatmapChartStatus = useReportStore((s) => s.actions.setDataHeatmapChartStatus);
  const currentDataHeatmapChartStatus = (dataHeatmapChartStatus as any)[activeButton];
  /**
   * Reads the rhythm events newly created on the tab without subscribing to them. Applies the event-news selector for the TAB_1 key to the current store snapshot.
   *
   * @returns The array of newly created events for the tab.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  /**
   * Reads the tab's edited rhythm events straight from the report store rather than from a subscribed value. Applies the event-changes selector for the TAB_1 key to the current store snapshot.
   *
   * @returns The array of edited events recorded for the tab.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const setCombinedEventsInHeatmap = useReportStore((s) => s.actions.setCombinedEventsInHeatmap);

  const { profileId, studyId, startReport, stopReport, timezoneOffset } = useReportStore(
    (s) => s.reportInfo,
  ) as IReportInfo;
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);

  /**
   * Fetches the artifact events covering one calendar day so the RR heatmap knows where the signal is unusable. Queries holter events for the study and profile across that day, formats them into strips, then overlays the tab's local event edits and new events restricted to the day window and the artifact type.
   *
   * @param date - The start of the day to fetch, used as the lower bound of the search window.
   * @returns A promise resolving to a pair of the day's ISO timestamp and its reconciled artifact events.
   */
  const handleFetchHolterEvents = async (date: any) => {
    const filter = {
      studyId,
      profileId,
      types: [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT],
      startSearchDate: date,
      stopSearchDate: moment(date).add(1, 'days').toISOString(),
    };
    const { events } = await fetchHolterEvents(filter, 0, true, null, false);
    return [
      new Date(date).toISOString(),
      updateEventChangeAndNew(
        formatEventStrip(events),
        getEventChanges(),
        getEventNews(),
        filter.startSearchDate,
        filter.stopSearchDate,
        [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT],
      ),
    ];
  };

  const callbackListDate = async (listDate: any[]) => {
    if (!_.isEmpty(listDate)) {
      const promiseEvents = _.map(listDate, (date) => handleFetchHolterEvents(date));
      const listEventsByDate = await Promise.all(promiseEvents);
      setCombinedEventsInHeatmap((prev: any) => {
        const clonePrev: any = { ...prev };
        _.forEach(listEventsByDate, (item) => {
          clonePrev[item[0]] = item[1];
        });
        return clonePrev;
      });
    }
  };

  /**
   * Loads the RR heatmap counts for the currently active beat button. Returns immediately unless that button's status is initial or loading, marks it loading, fetches and formats the counts, sets the status to empty or success accordingly, and on failure logs, shows an error toast and rewinds the status to initial.
   *
   * @returns A promise resolving once the heatmap data or its failure status has been stored.
   */
  const fetchChartData = async () => {
    if (
      currentDataHeatmapChartStatus !== DATA_HEATMAP_STATUS.INITIAL &&
      currentDataHeatmapChartStatus !== DATA_HEATMAP_STATUS.LOADING
    ) {
      return;
    }
    setDataHeatmapChartStatus((prevState: any) => ({
      ...prevState,
      [activeButton]: DATA_HEATMAP_STATUS.LOADING,
    }));
    try {
      const filter = {
        studyId,
        profileId,
        rrHeatMapType: activeButton,
      };
      const result = await fetchHolterRrHeatMapCount(filter, null, false);
      const dataHeatmapChart = formatHolterRrHeatMapCount(result);
      if (_.isEmpty(dataHeatmapChart)) {
        setDataHeatmapChartStatus((prevState: any) => ({
          ...prevState,
          [activeButton]: DATA_HEATMAP_STATUS.EMPTY,
        }));
        return;
      }
      setDataHeatmapChartStatus((prevState: any) => ({
        ...prevState,
        [activeButton]: DATA_HEATMAP_STATUS.SUCCESS,
      }));

      setDataHeatmapChart((prevState: any) => ({
        ...prevState,
        [activeButton]: dataHeatmapChart,
      }));
    } catch (error) {
      logError('Failed to fetch holter RR heatmap: ', error);
      toastrError('Failed to fetch holter RR heatmap', 'Error');
      setDataHeatmapChartStatus((prevState: any) => ({
        ...prevState,
        [activeButton]: DATA_HEATMAP_STATUS.INITIAL,
      }));
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [activeButton]);

  return {
    activeButton,
    currentDataHeatmapChartStatus,
    profileId,
    studyId,
    startReport,
    stopReport,
    timezoneOffset,
    ecgDataMap,
    callbackListDate,
  };
};

export default useRrInterval;
